import { LoadError, Pill, SectionTitle, Skeleton, Sparkline, StatTile, show, useLoad, when } from "./ui";

// Is Remy working, and are people coming back. Totals for everyone; no names.

export default function AdminHealth() {
  const { data: m, error, loading, reload } = useLoad("/admin/metrics");
  const work = useLoad("/admin/worker");
  const log = useLoad<any[]>("/admin/log");
  if (loading && !m) return <Skeleton rows={4} />;
  if (error && !m) return <LoadError message={error} onRetry={reload} />;
  const worker = work.loading && !work.data ? "checking" : work.data?.status;
  const pct = (v: any) => (v === null || v === undefined ? null : `${v}%`);
  const money = (v: any) => (v === null || v === undefined ? null : `$${Number(v).toFixed(2)}`);
  const users = (m?.daily_active || []).map((d: any) => d.users);

  return (
    <div data-testid="admin-health">
      <SectionTitle kicker="Health" title="Is Remy running" />
      <div className="flex items-center justify-between gap-3 rounded-2xl bg-secondary/40 px-4 py-3">
        <span className="text-sm font-semibold">Background worker (reminders, repeating texts, email checks)</span>
        {worker === "ok" ? <Pill tone="good">Running</Pill>
          : worker === "down" ? <Pill tone="attention">Not running</Pill>
          : worker === "checking" ? <Pill>Checking…</Pill>
          : <Pill>Couldn't check</Pill>}
      </div>

      <SectionTitle title="Last 30 days" note="Counts only. No message content is kept here." />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="admin-scorecard">
        <StatTile label="People active this week" value={m?.active_users_last_7_days}>
          <Sparkline values={users} label="People active per day, last 30 days" className="text-primary" />
        </StatTile>
        <StatTile label="Texts to Remy" value={m?.messages_in}>
          <Sparkline values={m?.daily_texts || []} label="Texts per day, last 30 days" className="text-primary" />
        </StatTile>
        <StatTile label="Replies that failed" value={m?.replies_failed} tone={m?.replies_failed ? "attention" : undefined}>
          <Sparkline values={m?.daily_failed || []} label="Failed replies per day, last 30 days"
            className="text-muted-foreground" />
        </StatTile>
        <StatTile label="People texting 4+ days this week" value={m?.habit_users_last_7_days} />
        <StatTile label="Reminders sent on time" value={pct(m?.reminders_on_time_pct)} />
        <StatTile label="Reminders answered within 2 hours" value={pct(m?.reminders_answered_pct)} />
        <StatTile label="Texts Remy sent" value={m?.texts_out} />
        <StatTile label="AI cost" value={money(m?.ai_cost_usd)} />
        <StatTile label="AI cost per active person" value={money(m?.ai_cost_per_active_user_usd)} />
      </div>

      <SectionTitle title="Asked for, Remy couldn't do"
        note={`${show(m?.unmet_total)} in the last 30 days, everyone together.`} />
      {Object.keys(m?.unmet_requests || {}).length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing yet.</p>
      ) : (
        <div className="rounded-3xl border border-border bg-card p-5" data-testid="admin-unmet">
          <div className="text-xs font-bold">By kind</div>
          {Object.entries(m.unmet_requests).map(([cat, n]) => (
            <div key={cat} className="mt-1.5 flex justify-between gap-3 text-sm">
              <span className="capitalize">{cat.replace(/_/g, " ")}</span>
              <span className="tabular-nums text-muted-foreground">{String(n)}</span>
            </div>
          ))}
          {(m.unmet_examples || []).length > 0 && (
            <div className="mt-4 border-t border-border pt-4" data-testid="admin-unmet-examples">
              <div className="text-xs font-bold">What they wanted, in Remy's words (no names)</div>
              {(m.unmet_examples || []).map((e: any) => (
                <div key={e.what} className="mt-1.5 flex justify-between gap-3 text-sm">
                  <span>{e.what}</span>
                  <span className="tabular-nums text-muted-foreground">{String(e.count)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <SectionTitle title="What you changed"
        note="Every change made from this admin area: what, when, and whose account." />
      {log.loading && !log.data ? <Skeleton rows={2} />
        : (log.data || []).length === 0 ? <p className="text-sm text-muted-foreground">Nothing yet.</p> : (
        <div className="overflow-hidden rounded-2xl border border-border bg-card" data-testid="admin-log">
          {(log.data || []).slice(0, 40).map((e, i) => (
            <div key={i} className={`flex min-h-[48px] items-center justify-between gap-3 px-4 py-2 text-sm ${i ? "border-t border-border" : ""}`}>
              <span className="min-w-0">
                <span className="block">{e.what}</span>
                {e.who && <span className="block truncate text-xs text-muted-foreground">{e.who}</span>}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">{when(e.at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
